import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { getTaxonomyNodes } from "../../api/adminApi";
import "./workspace.css";

/**
 * The two ORGANIZATION-owned layers, as browsable lenses over the same documents.
 *
 * `mode="taxonomy"` walks the controlled vocabulary — what a document is ABOUT. Terms are multi-valued
 * and are applied by a JOIN on the server, not by a column in the filter expression, so selecting one
 * runs a permissive expression scoped by the term rather than trying to express it as a query.
 *
 * `mode="bases"` lists the document bases themselves — the bottom layer, where the bytes physically
 * live. Opening one scopes an ordinary search to that base.
 *
 * Neither lens is a separate document population: both end in the normal results view, so a document
 * found through a term looks and behaves exactly like one found by typing a query.
 */
export default function OrgLayerExplorer({ mode, dbs, onRunSearch }) {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [terms, setTerms] = useState([]);
  const [cwd, setCwd] = useState(null);
  const [loading, setLoading] = useState(mode === "taxonomy");

  useEffect(() => {
    if (mode !== "taxonomy" || !token) return;
    let alive = true;
    setLoading(true);
    getTaxonomyNodes(token)
      .then((list) => {
        if (!alive) return;
        setTerms((Array.isArray(list) ? list : []).filter((n) => n?.isActive));
      })
      .catch(() => alive && setTerms([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [mode, token]);

  const byId = useMemo(() => {
    const m = new Map();
    terms.forEach((n) => m.set(n.id, n));
    return m;
  }, [terms]);

  const children = useMemo(
    () => terms
      .filter((n) => (n.parentId ?? null) === cwd)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name || "").localeCompare(b.name || "")),
    [terms, cwd]
  );

  const crumbs = useMemo(() => {
    const chain = [];
    let c = cwd != null ? byId.get(cwd) : null;
    let guard = 0;
    while (c && guard++ < 100) {
      chain.unshift(c);
      c = c.parentId != null ? byId.get(c.parentId) : null;
    }
    return chain;
  }, [cwd, byId]);

  if (mode === "bases") {
    const list = (dbs || []).filter((d) => d && d.dbName);
    return (
      <div>
        <div className="ws-head">
          <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
            {t("workspace.layerBases", { defaultValue: "Document bases" })}
          </span>
        </div>
        {list.length === 0 ? (
          <div className="ws-empty">{t("workspace.noBases", { defaultValue: "No document bases are available to you." })}</div>
        ) : (
          <div className="ws-tiles">
            {list.map((d) => (
              <button
                key={d.dbName}
                className="ws-tile"
                title={d.dbName}
                // A permissive expression: an empty one matches nothing, and the point here is
                // "show me everything in this base".
                onClick={() => onRunSearch(`{"Id":{"$gt":0}} in ${d.dbName}`)}
              >
                <BaseGlyph />
                <span className="ws-tile__name">{d.displayName || d.dbName}</span>
                {d.displayName && d.displayName !== d.dbName && (
                  <span className="ws-tile__sub">{d.dbName}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── taxonomy ────────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="ws-empty">{t("common.loading", { defaultValue: "Loading…" })}</div>;
  }

  return (
    <div>
      <div className="ws-head">
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setCwd(null)} style={crumbStyle(cwd == null)}>
            {t("workspace.allSubjects", { defaultValue: "Subjects" })}
          </button>
          {crumbs.map((c) => (
            <React.Fragment key={c.id}>
              <span style={{ color: "#9ca3af" }}>›</span>
              <button onClick={() => setCwd(c.id)} style={crumbStyle(c.id === cwd)}>{c.name}</button>
            </React.Fragment>
          ))}
        </div>
        {cwd != null && (
          <div className="ws-head__actions">
            <button
              className="ws-btn ws-btn--primary"
              onClick={() => onRunSearch('{"Id":{"$gt":0}}', { taxonomyNodeIds: [cwd] })}
            >
              {t("workspace.showDocuments", { defaultValue: "Show documents" })}
            </button>
          </div>
        )}
      </div>

      {terms.length === 0 ? (
        <div className="ws-empty">
          {t("workspace.noTerms", { defaultValue: "No subjects have been set up yet." })}
          <div className="ws-empty__hint">
            {t("workspace.noTermsHint", { defaultValue: "An administrator defines these under Content Organization." })}
          </div>
        </div>
      ) : children.length === 0 ? (
        <div className="ws-empty">
          {t("workspace.noSubTerms", { defaultValue: "Nothing filed under this subject." })}
        </div>
      ) : (
        <div className="ws-tiles">
          {children.map((n) => (
            <button
              key={n.id}
              className="ws-tile"
              title={n.path}
              onClick={() => setCwd(n.id)}
            >
              <TermGlyph color={n.color} />
              <span className="ws-tile__name">{n.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function crumbStyle(active) {
  return {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: active ? "#111827" : "#4338ca",
    fontWeight: 600,
    padding: 0,
    fontSize: 14,
  };
}

function BaseGlyph() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#8497c4" strokeWidth="1.4">
      <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" /><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
    </svg>
  );
}

function TermGlyph({ color }) {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={color || "#8497c4"} strokeWidth="1.4">
      <path d="M20.6 13.4 12 22l-9-9V3h10z" /><circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}
